// v0.14.1 hotfix — tenant-timezone-aware datetime formatting.
//
// Accepts either:
//   * the legacy plain ISO string (e.g. "2026-05-13T05:35:59+00:00") —
//     this is what every existing endpoint currently returns
//   * the structured object { utc, local, timezone } — new endpoints
//     that opt in to format_timestamp() on the server return this
//
// Either way, the function renders the timestamp in the tenant's
// preferred IANA timezone via Intl.DateTimeFormat. If no timezone is
// available (because we got a raw string), fall back to Asia/Kolkata.

import { useTenantStore } from "@/store/tenant";

export type StructuredTimestamp = {
  utc: string;
  local: string;
  timezone: string;
};

export type TimestampInput = StructuredTimestamp | string | null | undefined;

type Format = "date" | "datetime" | "time" | "relative";

const DEFAULT_TZ = "Asia/Kolkata";

export function formatTimestamp(
  ts: TimestampInput,
  format: Format = "datetime",
  fallbackTz: string = DEFAULT_TZ,
): string {
  if (!ts) return "—";

  let isoStr: string;
  let tz: string;
  if (typeof ts === "object") {
    // Use the .local field if present; Intl will still render in `tz`
    // so this is just a parseable ISO. `.utc` works equally well.
    isoStr = ts.local || ts.utc;
    tz = ts.timezone || fallbackTz;
  } else {
    isoStr = ts;
    tz = fallbackTz;
  }

  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return "—";

  if (format === "relative") {
    return formatRelative(date);
  }

  const options: Intl.DateTimeFormatOptions = { timeZone: tz };
  if (format === "date") {
    options.day = "2-digit";
    options.month = "short";
    options.year = "numeric";
  } else if (format === "datetime") {
    options.day = "2-digit";
    options.month = "short";
    options.year = "numeric";
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.hour12 = true;
  } else if (format === "time") {
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.hour12 = true;
  }
  return new Intl.DateTimeFormat("en-IN", options).format(date);
}

function formatRelative(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}

// Convenience hook so call sites can avoid threading the tz prop —
// pulls the current tenant timezone from the Zustand store.
export function useFormatTimestamp() {
  const tz = useTenantStore((s) => s.timezone) || DEFAULT_TZ;
  return (ts: TimestampInput, format: Format = "datetime") =>
    formatTimestamp(ts, format, tz);
}
